import { QUIET_PAYWALL } from "@/utils/paymentRequired";
import apiClient from "./apiClient";

/**
 * For a page that shows its own offer when the account is not entitled. The
 * 402 is still thrown; only the app-wide upgrade dialog stays shut.
 */
export interface PaywallOptions {
  quietPaywall?: boolean;
}

const paywallConfig = (options?: PaywallOptions) =>
  options?.quietPaywall ? { [QUIET_PAYWALL]: true } : {};

const CACHE_TTL_MS = 10 * 60 * 1000;
const responseCache = new Map<string, { expiresAt: number; value: unknown }>();
const inFlightRequests = new Map<string, Promise<unknown>>();

function getCachedValue<T>(key: string, allowExpired = false): T | null {
  const cached = responseCache.get(key);
  if (!cached) return null;
  if (!allowExpired && cached.expiresAt < Date.now()) {
    responseCache.delete(key);
    return null;
  }
  return cached.value as T;
}

function setCachedValue<T>(key: string, value: T) {
  responseCache.set(key, {
    value,
    expiresAt: Date.now() + CACHE_TTL_MS,
  });
}

async function cachedRequest<T>(
  key: string,
  requestFn: () => Promise<T>,
): Promise<T> {
  const cached = getCachedValue<T>(key);
  if (cached) return cached;

  const existingRequest = inFlightRequests.get(key);
  if (existingRequest) return existingRequest as Promise<T>;

  const request = requestFn()
    .then((data) => {
      setCachedValue(key, data);
      return data;
    })
    .catch((error) => {
      const fallback = getCachedValue<T>(key, true);
      if (fallback) return fallback;
      throw error;
    })
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, request as Promise<unknown>);
  return request;
}

export interface Vaccination {
  name: string;
  code: string;
  required: boolean;
  timing: string;
}

export interface HealthWarning {
  level: "low" | "medium" | "high";
  type: string;
  message: string;
}

export interface HealthRequirements {
  country: string;
  lastUpdated: string;
  source: string;
  vaccinations: Vaccination[];
  healthWarnings: HealthWarning[];
  malariaRisk: string;
  recommendations: string[];
}

// ─── Rich Health Advice Types (from HealthRecommendationsService) ─────────────

export interface VaccinationRequirement {
  vaccineName: string;
  isoCode: string;
  isRequired: boolean;
  isRecommended: boolean;
  minDaysBeforeDeparture?: number;
  validityDays?: number;
  boosterRequired?: boolean;
  source: { name: string; url: string };
}

export interface HealthRecommendation {
  id: string;
  category:
    | "vaccination"
    | "medication"
    | "precaution"
    | "advisory"
    | "warning";
  severity: "critical" | "high" | "medium" | "low" | "info";
  title: string;
  description: string;
  details?: string;
  evidenceLevel: "mandatory" | "recommended" | "suggested" | "optional";
  source: { name: string; url: string; credibilityScore: number };
}

export interface DiseaseOutbreak {
  id: string;
  disease: string;
  country: string;
  region?: string;
  severity: "critical" | "high" | "medium" | "low";
  casesReported: number;
  lastReportDate: string;
  trendDirection: "increasing" | "stable" | "decreasing";
  affectsTravel: boolean;
  source: { name: string };
}

export interface AggregatedHealthAdvice {
  profile: {
    destinationCountry: string;
    travelerNationality: string;
    departureDate: string;
    returnDate: string;
    travelType: string;
    travelerAge?: number;
  };
  riskLevel: "low" | "medium" | "high" | "critical";
  recommendations: HealthRecommendation[];
  requiredVaccinations: VaccinationRequirement[];
  recommendedVaccinations: VaccinationRequirement[];
  activeOutbreaks: DiseaseOutbreak[];
  lastUpdated: string;
  sources: { name: string; url: string }[];
}

export interface ActivityRisk {
  activity: string;
  category: "sport" | "environment" | "medical" | "equipment";
  severity: "critical" | "high" | "medium" | "low";
  title: string;
  description: string;
  recommendations: string[];
  requiredEquipment?: string[];
  requiredClearances?: string[];
}

export interface AiTripAnalysis {
  climateRiskSummary: string;
  transportRisks?: string[];
  activityRisks?: string[];
  healthProfileRisks: string[];
  personalizedRecommendations: string[];
  urgentActions: string[];
  overallRiskLevel: "low" | "medium" | "high" | "critical";
  disclaimer: string;
  localTransportRisks?: ActivityRisk[];
  localActivityRisks?: ActivityRisk[];
}

// ─── API Functions ─────────────────────────────────────────────────────────────

/**
 * Get comprehensive health requirements for a country (static/cached data)
 */
export async function getHealthRequirements(
  country: string,
  departureDate?: string,
  options?: PaywallOptions,
): Promise<HealthRequirements> {
  const params = departureDate ? { departureDate } : {};
  const cacheKey = `requirements:${country}:${departureDate ?? "none"}`;

  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get(
      `/travel-health/requirements/${encodeURIComponent(country)}`,
      { params, ...paywallConfig(options) },
    );
    return response.data.data;
  });
}

/**
 * Get aggregated, multi-source health advice for a trip profile
 */
export async function getHealthAdvice(
  destinationCountry: string,
  departureDate: string,
  returnDate: string,
  travelType: string = "flight",
  residenceCountry?: string,
  options?: PaywallOptions,
): Promise<AggregatedHealthAdvice> {
  const params: Record<string, string> = {
    departureDate,
    returnDate,
    travelType,
  };
  if (residenceCountry) params.residenceCountry = residenceCountry;
  const response = await apiClient.get(
    `/health-recommendations/destination/${encodeURIComponent(destinationCountry)}`,
    { params, ...paywallConfig(options) },
  );
  return response.data.data;
}

/**
 * Get required vaccinations for a country
 */
export async function getRequiredVaccinations(
  country: string,
): Promise<Vaccination[]> {
  const response = await apiClient.get(
    `/travel-health/vaccinations/${encodeURIComponent(country)}`,
  );
  return response.data.data;
}

/**
 * Get health warnings for a country
 */
export async function getHealthWarnings(
  country: string,
  options?: PaywallOptions,
): Promise<HealthWarning[]> {
  const cacheKey = `warnings:${country}`;

  return cachedRequest(cacheKey, async () => {
    const response = await apiClient.get(
      `/travel-health/warnings/${encodeURIComponent(country)}`,
      paywallConfig(options),
    );
    return response.data.data;
  });
}

/**
 * AI-powered trip health analysis (climate + health profile)
 */
export async function getAiTripAnalysis(
  travelId: string,
): Promise<AiTripAnalysis> {
  const response = await apiClient.post(
    `/health-recommendations/ai-trip-analysis`,
    { travelId },
  );
  return response.data.data;
}

/**
 * Trip readiness (UX_CONCEPT.md §6.1). The backend computes the summary
 * (backend/src/services/tripReadiness.ts); this side only shows it.
 */
export type ReadinessState = "unchecked" | "in_progress" | "ready";

export interface ReadinessSummary {
  state: ReadinessState;
  total: number;
  done: number;
  /** 0–100, or null while no checklist has been built. */
  percent: number | null;
  urgentOpen: number;
}

export interface ReadinessItem {
  id: string;
  category: string;
  task: string;
  completed: boolean;
  urgent: boolean;
  source?: string;
  /** The traveller says this item does not apply to them. */
  notForMe?: boolean;
}

export interface TripReadiness {
  checklist: ReadinessItem[];
  summary: ReadinessSummary;
  /** Set on the save that brought the trip to 100% and earned its stamp. */
  stampEarned?: StampEarned | null;
}

/**
 * The travel passport (UX_CONCEPT.md §7). The backend awards stamps and
 * works out the level (backend/src/services/passport.ts).
 */
export type PassportLevel = "starter" | "explorer" | "globetrotter" | "nomad";

export interface PassportStamp {
  travelId: string;
  city: string;
  country: string | null;
  departureDate: string | null;
  earnedAt: string;
}

export interface StampEarned {
  stamp: PassportStamp;
  /** Stamps in the passport, this one included. */
  count: number;
  level: PassportLevel;
  /** The level just reached, when this stamp crossed into it. */
  levelUp: PassportLevel | null;
}

export interface Passport {
  /** Newest first. */
  stamps: PassportStamp[];
  count: number;
  level: PassportLevel;
  next: { level: PassportLevel; at: number } | null;
}

export async function getPassport(): Promise<Passport> {
  const response = await apiClient.get(`/travels/passport`);
  return response.data.data;
}

/** The stored checklist and its summary; an empty checklist means none yet. */
export async function getReadiness(travelId: string): Promise<TripReadiness> {
  const response = await apiClient.get(`/travels/${travelId}/readiness`);
  return response.data.data;
}

/** Saves the whole checklist — after building it, and after adding an item. */
export async function saveReadinessChecklist(
  travelId: string,
  checklist: ReadinessItem[],
): Promise<TripReadiness> {
  const response = await apiClient.put(`/travels/${travelId}/readiness`, {
    checklist,
  });
  return response.data.data;
}

/** Marks one stored item done / not done, or not for me. */
export async function updateReadinessItem(
  travelId: string,
  itemId: string,
  change: { completed?: boolean; notForMe?: boolean },
): Promise<{ summary: ReadinessSummary; stampEarned: StampEarned | null }> {
  const response = await apiClient.patch(
    `/travels/${travelId}/readiness/${itemId}`,
    change,
  );
  const data = response.data.data;
  return { summary: data.summary, stampEarned: data.stampEarned ?? null };
}

/** Readiness of every trip that has a checklist, keyed by trip id. */
export async function getReadinessSummaries(): Promise<
  Record<string, ReadinessSummary>
> {
  const response = await apiClient.get(`/travels/readiness`);
  return response.data.data?.summaries ?? {};
}

/**
 * Names of the vaccines the traveller has on record, lower-cased. Empty when
 * the plan does not include immunizations — the checklist is then built
 * without them, rather than an upgrade dialog opening over it.
 */
export async function getRecordedVaccineNames(): Promise<string[]> {
  try {
    const response = await apiClient.get(
      `/immunizations`,
      paywallConfig({ quietPaywall: true }),
    );
    const list = (response.data?.data?.immunizations ?? []) as Array<{
      vaccineName?: string;
    }>;
    return list
      .map((i) => (i.vaccineName ?? "").toLowerCase().trim())
      .filter(Boolean);
  } catch {
    return [];
  }
}

// ─── New AC 6, 7, 8, 9, 19 Types ─────────────────────────────────────────────

export interface OperatorHealthData {
  operatorName: string | null;
  travelType: string;
  destination: string | null;
  operatorAdvisory: string | null;
  transportRisks: ActivityRisk[];
  cruiseHealthRequirements: Array<{
    category: string;
    title: string;
    description: string;
    severity: string;
    source: string;
  }>;
  generalAdvisory: string;
}

export interface ForumHealthSummary {
  commonHealthIssues: string[];
  foodWaterSafety: string;
  localHealthcareSummary: string;
  travelerTips: string[];
  unexpectedRisks: string[];
  disclaimer: string;
  suggestedForumSearches: string[];
}

export interface HealthcareFacility {
  id: number;
  name: string;
  type: string;
  lat: number;
  lon: number;
  phone: string | null;
  website: string | null;
  openingHours: string | null;
  emergency: string | null;
  operator: string | null;
  address: string | null;
  distanceM: number;
}

export interface FacilitiesResult {
  facilities: HealthcareFacility[];
  center: { lat: number; lon: number };
  radiusM: number;
  counts: Record<string, number>;
}

/**
 * AC 7+8: Get deterministic activity and transport mode risks
 */
export async function getActivityRisks(
  travelType: string,
  activities: string[],
): Promise<{ transportRisks: ActivityRisk[]; activityRisks: ActivityRisk[] }> {
  const params = new URLSearchParams({
    travelType,
    activities: activities.join(","),
  });
  const response = await apiClient.get(
    `/health-recommendations/activity-risks?${params}`,
  );
  return {
    transportRisks: response.data.data.transportRisks ?? [],
    activityRisks: response.data.data.activityRisks ?? [],
  };
}

/**
 * AC 6: Get tour operator health data
 */
export async function getOperatorHealthData(
  travelType: string,
  operatorName?: string,
  destination?: string,
): Promise<OperatorHealthData> {
  const params = new URLSearchParams({ travelType });
  if (operatorName) params.set("operatorName", operatorName);
  if (destination) params.set("destination", destination);
  const response = await apiClient.get(
    `/health-recommendations/operator?${params}`,
  );
  return response.data.data;
}

/**
 * AC 9: Get AI-generated forum health summary for destination
 */
export async function getForumHealthSummary(
  destination: string,
  country: string,
  activities: string[],
  travelType: string,
): Promise<ForumHealthSummary> {
  const response = await apiClient.post(
    `/health-recommendations/forum-summary`,
    {
      destination,
      country,
      activities,
      travelType,
    },
  );
  return response.data.data;
}

/**
 * AC 19: Get healthcare facilities near coordinates (OSM Overpass)
 */
export async function getHealthcareFacilities(
  lat: number,
  lon: number,
  radiusM = 5000,
  types = ["hospital", "pharmacy", "clinic"],
): Promise<FacilitiesResult> {
  const params = new URLSearchParams({
    lat: lat.toString(),
    lon: lon.toString(),
    radius: radiusM.toString(),
    types: types.join(","),
  });
  const response = await apiClient.get(
    `/health-recommendations/facilities?${params}`,
  );
  return response.data.data;
}

/**
 * AC 19: Geocode a city/country to coordinates (Nominatim via backend)
 */
export async function geocodeDestination(
  query: string,
): Promise<{ lat: number; lon: number; displayName: string }> {
  const response = await apiClient.get(
    `/health-recommendations/geocode?q=${encodeURIComponent(query)}`,
  );
  return response.data.data;
}
